import { useEffect, useRef, useState, type CSSProperties } from "react";
import { pickText, resolveAssetUrl, useTourState, useTourStoreApi } from "@heroncendre/player";
import { nordDe, type Espace, type Niveau } from "./espaces.js";
import { COULEURS, POLICE_UI_SPLASH } from "./splashStyles.js";

/**
 * ============================================================================
 *  SpacePlan.tsx — Le plan d'un espace, ses scènes, et où l'on regarde
 * ----------------------------------------------------------------------------
 *  Référence : `PlanGroupe.dc.html` du handoff « navigation carnet ».
 *
 *  ── Coordonnées ────────────────────────────────────────────────────────────
 *
 *  Les points sont en fraction de l'IMAGE D'ORIGINE (comme les écrit l'éditeur).
 *  Un espace sans plan propre montre un RECADRAGE de l'illustration du domaine :
 *  on ramène chaque point dans ce cadre, et l'image entière est agrandie et
 *  décalée en fond CSS pour n'en montrer que la partie voulue. Le cadre est
 *  ensuite ajusté en *contain* dans la boîte (mesurée par ResizeObserver).
 *
 *  ── Le cône de vision ──────────────────────────────────────────────────────
 *
 *  Il suit le cap RÉEL de la caméra, lu dans le store par abonnement et écrit
 *  directement sur l'élément : un rendu React à chaque degré serait absurde.
 *  Le cap 0 d'un panorama n'est pas le haut du plan : on retranche le nord de
 *  la scène (`espaces.json`), réglé une fois pour toutes — dans l'éditeur, par
 *  le panneau « Nord du plan » (`AlignementNord`), qui impose un nord en cours
 *  de réglage (`nord`) et fait tourner le cône à la souris (`onOrienter`).
 * ==========================================================================*/

const TAILLE_CONE = 100;

export interface SpacePlanProps {
  espace: Espace;
  assetBaseUrl: string;
  /** Scène courante, si elle est dans cet espace : point vert et cône. */
  sceneCourante?: string;
  /** Pastilles des espaces (plan du domaine seulement). `ici` : l'espace où l'on se trouve. */
  zones?: { espace: Espace; ouvrir: () => void; ici?: boolean }[];
  /**
   * Faux : pas de point pour les scènes de l'espace. Sur le plan du domaine, sa
   * pastille (1) dit déjà où sont ses vues de drone ; un losange en plus ferait
   * double emploi.
   */
  avecPoints?: boolean;
  /**
   * Le nom de la zone sélectionnée (`ici`) reste affiché à côté de son numéro.
   * Sans survol au doigt (téléphone), c'est le seul moyen de savoir laquelle l'est.
   */
  nomZoneSelectionnee?: boolean;
  /** Présent : bouton « ‹ Domaine » en haut à gauche. */
  retour?: { libelle: string; action: () => void };
  legende: string;
  libelleNiveaux: string;
  onChoisir: (sceneId: string) => void;
  /** Nord imposé (éditeur, réglage en cours) ; absent : celui de `espaces.json`. */
  nord?: number;
  /**
   * Éditeur : le plan devient une poignée. Glisser autour du point de la scène
   * courante oriente le cône ; reçoit l'angle visé, en degrés depuis le haut du
   * plan, dans le sens des aiguilles d'une montre.
   */
  onOrienter?: (angle: number) => void;
}

export function SpacePlan({
  espace,
  assetBaseUrl,
  sceneCourante,
  zones = [],
  avecPoints = true,
  nomZoneSelectionnee = false,
  retour,
  legende,
  libelleNiveaux,
  onChoisir,
  nord: nordImpose,
  onOrienter,
}: SpacePlanProps) {
  const store = useTourStoreApi();
  const language = useTourState((s) => s.context.language);
  const boite = useRef<HTMLDivElement>(null);
  const cone = useRef<HTMLSpanElement>(null);
  const [taille, setTaille] = useState({ l: 0, h: 0 });
  const [survol, setSurvol] = useState<string | null>(null);

  // Par défaut, le niveau de la scène courante ; l'utilisateur peut en changer.
  const niveauCourant = espace.scenes.find((e) => e.scene.id === sceneCourante)?.niveau ?? 0;
  const [niveauChoisi, setNiveauChoisi] = useState<number | null>(null);
  useEffect(() => setNiveauChoisi(null), [espace.id]);
  const rang = Math.min(niveauChoisi ?? niveauCourant, Math.max(0, espace.niveaux.length - 1));
  const niveau: Niveau | undefined = espace.niveaux[rang];

  useEffect(() => {
    const element = boite.current;
    if (!element) return;
    const observateur = new ResizeObserver(([entree]) => {
      if (!entree) return;
      const { width, height } = entree.contentRect;
      setTaille((avant) =>
        Math.abs(avant.l - width) > 0.5 || Math.abs(avant.h - height) > 0.5 ? { l: width, h: height } : avant,
      );
    });
    observateur.observe(element);
    return () => observateur.disconnect();
  }, []);

  // Le cône suit le cap, hors React.
  useEffect(() => {
    if (!sceneCourante) return;
    const nord = nordImpose ?? nordDe(sceneCourante);
    const poser = (cap: number) => {
      if (cone.current) cone.current.style.transform = `rotate(${cap - nord}deg)`;
    };
    poser(store.getState().view.yaw);
    return store.subscribe((state, avant) => {
      if (state.view.yaw !== avant.view.yaw) poser(state.view.yaw);
    });
  }, [store, sceneCourante, rang, nordImpose]);

  const [x0, y0, cl, ch] = niveau?.recadrage ?? [0, 0, 100, 100];
  const largeurImage = niveau?.image.width ?? 4;
  const hauteurImage = niveau?.image.height ?? 3;
  const rapport = (cl * largeurImage) / (ch * hauteurImage);
  const cadre =
    taille.l && taille.h
      ? (() => {
          const l = Math.min(taille.l, taille.h * rapport);
          return { width: l, height: l / rapport };
        })()
      : { width: "100%", height: "100%" };

  /** Point de l'image d'origine (0–1) → pourcentage du cadre recadré. */
  const local = (x: number, y: number): [number, number] => [((x * 100 - x0) / cl) * 100, ((y * 100 - y0) / ch) * 100];
  const dedans = ([x, y]: [number, number]) => x >= -2 && x <= 102 && y >= -2 && y <= 102;

  const points = espace.scenes
    .filter((e) => avecPoints && e.position && (espace.niveaux.length === 1 || e.niveau === rang))
    // Le numéro est le rang dans l'espace, celui de la liste : pas le rang parmi les points placés.
    .map((e) => ({ entree: e, numero: espace.scenes.indexOf(e) + 1, xy: local(e.position!.x, e.position!.y) }))
    .filter((p) => dedans(p.xy));
  const architecte = niveau?.style === "architecte";

  // Éditeur : l'angle du pointeur autour du point de la scène courante. Le cadre a
  // les proportions de l'image : un angle à l'écran est un angle sur le plan.
  const orientation = points.find((p) => p.entree.scene.id === sceneCourante)?.xy;
  const orienter = (event: { clientX: number; clientY: number; currentTarget: HTMLElement }) => {
    if (!orientation || !onOrienter) return;
    const cadreRect = event.currentTarget.getBoundingClientRect();
    const dx = event.clientX - (cadreRect.left + (orientation[0] / 100) * cadreRect.width);
    const dy = event.clientY - (cadreRect.top + (orientation[1] / 100) * cadreRect.height);
    if (Math.hypot(dx, dy) < 4) return;
    onOrienter((Math.atan2(dx, -dy) * 180) / Math.PI);
  };

  return (
    <div
      ref={boite}
      style={{
        ...boiteStyle,
        background: architecte ? "#EFE6D5" : "#E3D2B0",
      }}
    >
      <div style={{ position: "relative", flex: "none", ...cadre }}>
        {niveau && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url("${resolveAssetUrl(assetBaseUrl, niveau.image.key)}")`,
              backgroundSize: `${10000 / cl}% ${10000 / ch}%`,
              backgroundPosition: `${cl >= 100 ? 0 : (x0 / (100 - cl)) * 100}% ${ch >= 100 ? 0 : (y0 / (100 - ch)) * 100}%`,
              backgroundRepeat: "no-repeat",
              mixBlendMode: architecte ? "multiply" : "normal",
            }}
          />
        )}

        <svg
          aria-hidden
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}
        >
          <polyline
            points={points.map((p) => p.xy.join(",")).join(" ")}
            fill="none"
            stroke="rgba(34,56,47,.7)"
            strokeWidth={1.4}
            strokeDasharray="3 4"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {zones.map(({ espace: cible, ouvrir, ici = false }) => {
          if (!cible.zone) return null;
          const [x, y] = local(cible.zone.position.x, cible.zone.position.y);
          const cle = `zone-${cible.id}`;
          const actif = survol === cle;
          const nom = pickText(cible.titre, language) ?? cible.id;
          return (
            <button
              key={cle}
              type="button"
              className="sn-cible"
              aria-label={`${cible.zone.numero} · ${nom}`}
              onClick={ouvrir}
              onMouseEnter={() => setSurvol(cle)}
              onMouseLeave={() => setSurvol((s) => (s === cle ? null : s))}
              onFocus={() => setSurvol(cle)}
              onBlur={() => setSurvol((s) => (s === cle ? null : s))}
              style={{ ...cible44, left: `${x}%`, top: `${y}%` }}
            >
              {/* Trois états : ailleurs (vert sombre, halo clair qui appelle), l'espace où
                  l'on se trouve (fond clair, chiffre sombre, sans halo), survolé (crème, agrandi). */}
              <span
                aria-hidden
                style={{
                  ...pastille,
                  width: actif ? 30 : 26,
                  height: actif ? 30 : 26,
                  background: actif ? COULEURS.creme : ici ? COULEURS.sauge : COULEURS.vert,
                  color: actif || ici ? COULEURS.vert : COULEURS.creme,
                  boxShadow:
                    !actif && !ici
                      ? "0 0 0 2px #F6EEE3, 0 0 12px 3px rgba(246,238,227,.75)"
                      : pastille.boxShadow,
                }}
              >
                {cible.zone.numero}
              </span>
              <Bulle visible={actif || (nomZoneSelectionnee && ici)} x={x} y={y} sombre>
                {nom}
              </Bulle>
            </button>
          );
        })}

        {points.map(({ entree, numero, xy: [x, y] }) => {
          const id = entree.scene.id;
          const courant = id === sceneCourante;
          const actif = survol === id;
          const nom = `${numero} · ${pickText(entree.scene.title, language) ?? id}`;
          return (
            <button
              key={id}
              type="button"
              className="sn-cible"
              aria-label={nom}
              aria-current={courant ? "location" : undefined}
              onClick={() => onChoisir(id)}
              onMouseEnter={() => setSurvol(id)}
              onMouseLeave={() => setSurvol((s) => (s === id ? null : s))}
              onFocus={() => setSurvol(id)}
              onBlur={() => setSurvol((s) => (s === id ? null : s))}
              style={{
                ...cible44,
                left: `${x}%`,
                top: `${y}%`,
                borderRadius: "50%",
                boxShadow: actif && !courant ? "0 0 0 1.5px rgba(34,56,47,.55)" : "none",
              }}
            >
              {courant && <span ref={cone} aria-hidden style={coneStyle} />}
              <span
                aria-hidden
                style={{
                  position: "relative",
                  width: courant ? 14 : actif ? 13 : 11,
                  height: courant ? 14 : actif ? 13 : 11,
                  transform: "rotate(45deg)",
                  background: courant ? COULEURS.vert : COULEURS.creme,
                  boxShadow: courant
                    ? "0 0 0 2px #F6EEE3, 0 1px 6px rgba(20,30,26,.5)"
                    : "0 0 0 1.5px #22382F, 0 1px 4px rgba(20,30,26,.35)",
                  transition: "width .2s, height .2s",
                }}
              />
              <Bulle visible={actif || (courant && survol === null)} x={x} y={y} sombre={!courant}>
                {nom}
              </Bulle>
            </button>
          );
        })}

        {onOrienter && orientation && (
          <div
            aria-hidden
            style={poigneeOrientation}
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
              orienter(event);
            }}
            onPointerMove={(event) => {
              if (event.currentTarget.hasPointerCapture(event.pointerId)) orienter(event);
            }}
          />
        )}
      </div>

      {retour && (
        <button type="button" className="sn-plan-bouton" onClick={retour.action} style={boutonRetour}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M15 5 8 12l7 7" />
          </svg>
          {retour.libelle}
        </button>
      )}

      {espace.niveaux.length > 1 && (
        <div role="group" aria-label={libelleNiveaux} style={niveauxStyle}>
          {espace.niveaux.map((n, i) => (
            <button
              key={n.groupeId}
              type="button"
              className="sn-plan-bouton"
              aria-pressed={i === rang}
              onClick={() => setNiveauChoisi(i)}
              style={{
                ...boutonNiveau,
                background: i === rang ? COULEURS.creme : "transparent",
                color: i === rang ? COULEURS.vert : COULEURS.creme,
              }}
            >
              {pickText(n.libelle, language) ?? String(i)}
            </button>
          ))}
        </div>
      )}

      <div style={legendeStyle}>{legende}</div>
    </div>
  );
}

/** Info-bulle d'un point ou d'une zone, retournée vers l'intérieur du plan près des bords. */
function Bulle({
  visible,
  x,
  y,
  sombre,
  children,
}: {
  visible: boolean;
  x: number;
  y: number;
  sombre: boolean;
  children: string;
}) {
  return (
    <span
      aria-hidden
      style={{
        position: "absolute",
        left: "50%",
        ...(y < 22 ? { top: 40 } : { bottom: 40 }),
        transform: x > 68 ? "translateX(-88%)" : x < 32 ? "translateX(-12%)" : "translateX(-50%)",
        whiteSpace: "nowrap",
        padding: "5px 9px",
        background: sombre ? COULEURS.vert : COULEURS.creme,
        color: sombre ? COULEURS.creme : COULEURS.vert,
        border: `1px solid ${COULEURS.vert}`,
        borderRadius: 2,
        fontSize: 12,
        fontWeight: 500,
        opacity: visible ? 1 : 0,
        pointerEvents: "none",
        transition: "opacity .2s",
        zIndex: 2,
      }}
    >
      {children}
    </span>
  );
}

/* --- styles (valeurs du handoff) ------------------------------------------ */

const boiteStyle: CSSProperties = {
  position: "relative",
  width: "100%",
  // Fixe dans la feuille du téléphone : c'est la liste qui cède, jamais le plan.
  flexShrink: 0,
  aspectRatio: "4 / 3",
  border: "1px solid rgba(246,238,227,.22)",
  borderRadius: 2,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: POLICE_UI_SPLASH,
};

/** Cible de 44 px centrée sur le point. */
const cible44: CSSProperties = {
  position: "absolute",
  width: 44,
  height: 44,
  margin: "-22px 0 0 -22px",
  appearance: "none",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const pastille: CSSProperties = {
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 0 0 2px #F6EEE3, 0 2px 8px rgba(20,30,26,.45)",
  fontSize: 12,
  fontWeight: 600,
  transition: "width .2s, height .2s, background .2s, box-shadow .2s",
};

const coneStyle: CSSProperties = {
  position: "absolute",
  left: "50%",
  top: "50%",
  width: TAILLE_CONE,
  height: TAILLE_CONE,
  marginLeft: -TAILLE_CONE / 2,
  marginTop: -TAILLE_CONE / 2,
  borderRadius: "50%",
  // 64° d'ouverture, centrés sur le haut (0° d'un conic-gradient).
  background: "conic-gradient(from -32deg, rgba(34,56,47,.6) 0deg 64deg, transparent 64deg)",
  maskImage: "radial-gradient(circle, #000 15%, transparent 70%)",
  WebkitMaskImage: "radial-gradient(circle, #000 15%, transparent 70%)",
  pointerEvents: "none",
};

/** Toute la surface du plan, au-dessus des points : on vise, on ne choisit pas. */
const poigneeOrientation: CSSProperties = {
  position: "absolute",
  inset: 0,
  cursor: "crosshair",
  touchAction: "none",
  zIndex: 3,
};

const boutonRetour: CSSProperties = {
  appearance: "none",
  position: "absolute",
  top: 6,
  left: 6,
  minHeight: 44,
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "0 12px 0 8px",
  background: "rgba(34,56,47,.9)",
  color: COULEURS.creme,
  border: "none",
  borderRadius: 2,
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: ".12em",
  textTransform: "uppercase",
  cursor: "pointer",
};

const niveauxStyle: CSSProperties = {
  position: "absolute",
  top: 6,
  right: 6,
  display: "flex",
  padding: 2,
  gap: 2,
  background: "rgba(34,56,47,.9)",
  borderRadius: 2,
};

const boutonNiveau: CSSProperties = {
  appearance: "none",
  minHeight: 40,
  minWidth: 44,
  padding: "0 12px",
  border: "none",
  borderRadius: 1,
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: ".12em",
  textTransform: "uppercase",
  cursor: "pointer",
};

const legendeStyle: CSSProperties = {
  position: "absolute",
  left: 6,
  bottom: 6,
  padding: "4px 8px",
  background: "rgba(246,238,227,.88)",
  color: COULEURS.vert,
  borderRadius: 2,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".14em",
  textTransform: "uppercase",
};
